import React, {
  useState,
  useRef,
  useEffect,
  FormEvent,
  useContext,
} from "react";
import { toSafeInteger, isEmpty } from "lodash";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { constantCase } from "util/strings";
import { useRouter } from "next/router";
import SignInModal from "components/componentLibrary/Modals/SignInModal";
import { analyticsEvents, track } from "util/analytics";
import Dots from "components/componentLibrary/LoadingDots";
import { publicSearchPageQuery_city_market_neighborhoods } from "types/pages/office-space/publicSearchPageQuery";
import { areaQueryWithSeoString_area } from "util/seo/types/areaQueryWithSeoString";
import {
  areaQueryWithCmsContent_area_Market_neighborhoods,
  areaQueryWithCmsContent_area_Neighborhood_market_neighborhoods,
} from "types/pages/office-space/m/areaQueryWithCmsContent";
import useIsCityInDb from "hooks/useIsCityInDb";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import {
  CreateRenterSearchActiveRequirementsMutation,
  determineLeaseTerm,
} from "components/ClientSearch/Filters/SaveActiveRequirementsButton";
import { EMAIL_REGEX, LEASE_TYPE } from "util/constants";
import { useCurrentUser } from "hooks";
import { trackChatStart, trackAccountCreate } from "util/ga4";

import styles from "./styles.module.scss";

const RENTER_ONBOARDING_MUTATION = gql`
  mutation RenterOnboardingChatModelCTAMutation(
    $name: String
    $email: String!
    $phone: String
    $cityOfInterest: CityName
    $marketOfInterest: ID
    $numberOfEmployees: Int
    $squareFeet: Int
    $neighborhoodIds: [ID]
    $neighborhoodNames: [String]
    $signUpType: String
    $areaExternalIds: [ID]
    $source: String
  ) {
    renterOnboarding(
      input: {
        renter: {
          phone: $phone
          name: $name
          email: $email
          signUpType: $signUpType
        }
        cityOfInterest: $cityOfInterest
        marketOfInterest: $marketOfInterest
        areaExternalIds: $areaExternalIds
        onboardingInfo: {
          source: $source
          neighborhoodIds: $neighborhoodIds
          numberOfEmployees: $numberOfEmployees
          squareFeet: $squareFeet
          neighborhoodNames: $neighborhoodNames
        }
      }
    ) {
      onboardingId
      created
      neighborhoodNames
      currentUser {
        id
        salesforceId
        email
        uuid
        firstName
        lastName
        phone
        e164Phone
        companyName
        displayName
        isRenter
        isBroker
        isVerified
        isAdmin
        hasPassword
        featureFlags
        profilePicURL
        brokerMarket {
          id
          name
          state
        }
        completedUserTasks {
          id
          task
        }
        confirmed
        unconfirmedEmail
        cityOfInterest
        marketOfInterest {
          id
          name
          position {
            lat
            lng
          }
        }
        tours {
          id
          date
        }
        latestCampaign {
          id
          uuid
          listings {
            id
          }
          surveys {
            nodes {
              id
              externalId
            }
          }
          currentActiveRequirements(setByUser: true) {
            id
            neighborhoods {
              id
              name
            }
            minPsf
            maxPsf
            minSquareFeet
            maxSquareFeet
            minMonthlyBudget
            maxMonthlyBudget
            numberOfEmployees
            minSqft: minSquareFeet
            maxSqft: maxSquareFeet
            maxPricePerSquareFoot: maxPsf
            minPricePerSquareFoot: minPsf
            maxMonthlyPrice: maxMonthlyBudget
            minMonthlyPrice: minMonthlyBudget
            leaseTerm
            areas {
              id
              externalId
              name
            }
            market {
              id
              name
              position {
                lat
                lng
              }
            }
          }
        }
      }
      errors {
        auth
      }
    }
  }
`;

interface FormElements extends HTMLFormControlsCollection {
  emailCta: HTMLInputElement;
  firstnameCta: HTMLInputElement;
  lastnameCta: HTMLInputElement;
  phoneCta: HTMLInputElement;
}

interface ChatCtaFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

interface IProps {
  loggedIn: boolean;
  market?: string;
  city?: string;
  numEmployees?: number;
  neighborhoods:
    | null
    | (
        | publicSearchPageQuery_city_market_neighborhoods
        | areaQueryWithCmsContent_area_Market_neighborhoods
        | areaQueryWithCmsContent_area_Market_neighborhoods
        | areaQueryWithCmsContent_area_Neighborhood_market_neighborhoods
      )[];
  submarkets: { id: string; externalId: string }[];
  className?: string;
  buttonText?: string;
}

const ChatModalCTA = ({
  loggedIn,
  market,
  city,
  submarkets,
  neighborhoods,
  numEmployees,
  className,
  buttonText = "CHAT",
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [showLoginModal, toggleLoginModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");
  const isCityInDB = useIsCityInDb(city);
  const { login } = useCurrentUser();
  const [onboardRenter, { loading }] = useMutation(RENTER_ONBOARDING_MUTATION);
  const [createRenterSearchActiveRequirements] = useMutation(
    CreateRenterSearchActiveRequirementsMutation
  );
  const router = useRouter();
  const { values } = useContext(QueryManagerContext);

  const getRedirectLink = () => {
    return `/tourbook/messages?message=${message}`;
  };

  const onSubmit = async (e: FormEvent<ChatCtaFormElement>) => {
    e.preventDefault();

    trackChatStart({
      listingId: "",
      pageUrl: router.asPath,
      buildingName: "",
      city: city || "",
    });

    if (loggedIn) {
      router.push(getRedirectLink());
      return;
    }

    const submarketIds = submarkets.map(({ externalId }) => externalId);
    const neighborhoodIds =
      neighborhoods?.map(({ externalId }) => externalId) || [];
    const areaExternalIds = [...submarketIds, ...neighborhoodIds];

    const variables = {
      email,
      phone,
      name: `${firstName} ${lastName}`,
      marketOfInterest: market,
      cityOfInterest: city && !market && isCityInDB ? constantCase(city) : null,
      numberOfEmployees: numEmployees,
      areaExternalIds,
      squareFeet: (numEmployees || 0) * 150,
      signUpType: "Public Search",
      source: "Chat",
    };

    const { data } = await onboardRenter({ variables });
    if (
      !data?.renterOnboarding?.created &&
      data?.renterOnboarding?.errors?.auth?.includes(
        "Login and password do not match."
      )
    ) {
      toggleLoginModal(true);
    } else if (data?.renterOnboarding?.currentUser) {
      if (data?.renterOnboarding?.created) {
        trackAccountCreate({
          sourcePage: window.location.pathname,
          city: city || undefined,
        });
      }
      if (!isEmpty(values) && router.asPath.includes("office-space/m/"))
        createRenterSearchActiveRequirements({
          variables: {
            marketExternalId: values.market.externalId,
            leaseTerm: determineLeaseTerm({
              leaseTermMin: values.leaseTermMin,
              leaseTermMax: values.leaseTermMax,
            }),
            statuses: [
              "available",
              "offers_in",
              "squarefoot_offers_in",
              "lease_out",
              "squarefoot_lease_out",
            ],
            leaseTypes: [...Object.keys(LEASE_TYPE), "UNKNOWN"],
            campaignId: data?.renterOnboarding?.currentUser.latestCampaign.id,
            minPricePerSquareFoot: values.minPricePerSquareFoot,
            maxPricePerSquareFoot: values.maxPricePerSquareFoot,
            minSquareFeet: values.minSqft,
            maxSquareFeet: values.maxSqft,
            minMonthlyBudget: values.minMonthlyPrice,
            maxMonthlyBudget: values.maxMonthlyPrice,
            numberOfEmployees: toSafeInteger(values.numEmployees),
            areaExternalIds: [
              ...values.submarkets.map(
                ({ externalId }: areaQueryWithSeoString_area) => externalId
              ),
              ...values.neighborhoods.map(
                ({ externalId }: areaQueryWithSeoString_area) => externalId
              ),
            ],
          },
        });
      await login();
      router.push(getRedirectLink());
    }
  };

  useEffect(() => {
    function handleClickAway(e: MouseEvent | TouchEvent) {
      let target: HTMLElement;
      if (e.target instanceof HTMLElement) {
        target = e.target;
        if (ref.current && !ref.current.contains(target) && showForm) {
          setShowForm(false);
        }
      }
    }

    document.addEventListener("mousedown", handleClickAway);
    document.addEventListener("touchstart", handleClickAway);

    return () => {
      document.removeEventListener("mousedown", handleClickAway);
      document.removeEventListener("touchstart", handleClickAway);
    };
  }, [ref, showForm]);

  const trackEvent = () =>
    track(analyticsEvents.clicked, {
      category: "Public Search CTAs",
      label: "Chat",
    });

  return (
    <div className={`${className} ${styles.chatPortalContainer}`}>
      {showForm && (
        <div ref={ref} className={styles.formContainer}>
          <button
            type="button"
            onClick={() => {
              setShowForm(false);
            }}
            className={styles.closeChatForm}
          >
            <span className="icon icon-cross" />
          </button>
          <form
            data-qa="chatCTAForm"
            action="POST"
            onSubmit={onSubmit}
            className={styles.chatForm}
          >
            {!loggedIn && (
              <>
                <input
                  type="text"
                  data-qa="chatCTAFirstName"
                  name="chatCtaFirstname"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.currentTarget.value);
                  }}
                  required
                />

                <input
                  type="text"
                  data-qa="chatCTALastName"
                  name="chatCtaLastname"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.currentTarget.value);
                  }}
                  required
                />

                <input
                  type="email"
                  pattern={EMAIL_REGEX}
                  title={`${email} is not a valid email`}
                  name="chatCtaEmail"
                  data-qa="chatCTAEmail"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.currentTarget.value);
                  }}
                  required
                />

                <input
                  type="tel"
                  name="chatCtaPhone"
                  data-qa="chatCTAPhone"
                  placeholder="Phone"
                  pattern="^[ ()+*#\-]*(?:\d[ ()+*#\-]*){7,40}$"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.currentTarget.value);
                  }}
                  required
                />
              </>
            )}

            <textarea
              name="Message"
              data-qa="chatCTAMessage"
              placeholder="Enter your message"
              value={message}
              onChange={(e) => {
                setMessage(e.currentTarget.value);
              }}
              maxLength={400}
              required
            />

            <button
              type="submit"
              data-qa="chatCTASubmit"
              className={styles.startChatButton}
            >
              {loading ? (
                <>
                  STARTING CHAT <Dots />
                </>
              ) : (
                <>START CHAT</>
              )}
            </button>
          </form>
        </div>
      )}

      {!showForm && (
        <div className={styles.buttonContainer}>
          <button
            data-jest="showCtaFormButton"
            data-qa="showChatCTAButton"
            name="showCTAChatForm"
            type="button"
            className="btn-primary"
            onClick={() => {
              trackEvent();
              setShowForm(true);
            }}
          >
            {buttonText}
          </button>
        </div>
      )}

      {showLoginModal && (
        <SignInModal
          existingEmail={email}
          onSuccess={() => {
            toggleLoginModal(false);
            router.push(getRedirectLink());
          }}
          onClose={() => {
            toggleLoginModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ChatModalCTA;
